import { useMemo } from "react";
import {
  resolveRoleColor,
  surfPreviewBackgroundStyle,
  surfPreviewScrimStyle,
  type SurfStyle,
} from "@vida/surf-core";

type PreviewTexts = {
  title: string;
  subtitle: string;
  kicker?: string;
};

type Props = {
  style: SurfStyle;
  previewImageUrl?: string | null;
  presentationType?: string;
  previewTexts?: PreviewTexts;
  onPreviewTextChange?: (patch: Partial<PreviewTexts>) => void;
};

const DEFAULT_SAFE = { x: 0.08, y: 0.12, w: 0.84, h: 0.76 };

export function StylePreviewFrame({
  style,
  previewImageUrl,
  previewTexts,
  onPreviewTextChange,
}: Props) {
  const safe = style.legibility.safeZones[0] ?? DEFAULT_SAFE;
  const scrim = surfPreviewScrimStyle(style);
  const editable = Boolean(onPreviewTextChange);

  const texts = useMemo(
    () => ({
      kicker: previewTexts?.kicker ?? "ANTEPRIMA STILE",
      title: previewTexts?.title ?? "Titolo di esempio",
      subtitle: previewTexts?.subtitle ?? "Sottotitolo campione",
    }),
    [previewTexts],
  );

  const layers = useMemo(
    () => [
      { role: "kicker" as const, text: texts.kicker, y: safe.y, h: 0.06 },
      { role: "title" as const, text: texts.title, y: safe.y + 0.07, h: 0.14 },
      { role: "subtitle" as const, text: texts.subtitle, y: safe.y + 0.22, h: 0.08 },
    ],
    [safe.y, texts],
  );

  return (
    <div className="style-preview-wrap">
      <div
        className="style-preview-slide"
        style={{
          ...surfPreviewBackgroundStyle(style, previewImageUrl),
          containerType: "size",
        }}
      >
        {scrim && <div className="style-preview-scrim" style={scrim} aria-hidden />}
        <div
          className="style-preview-safezone"
          style={{
            left: `${safe.x * 100}%`,
            top: `${safe.y * 100}%`,
            width: `${safe.w * 100}%`,
            height: `${safe.h * 100}%`,
          }}
          aria-hidden
        />
        {layers.map((layer) => {
          const tr = style.textRoles[layer.role];
          const color = resolveRoleColor(style, layer.role);
          const content = editable ? (
            <span
              className="style-preview-editable"
              contentEditable
              suppressContentEditableWarning
              role="textbox"
              aria-label={layer.role === "title" ? "Titolo anteprima" : layer.role === "subtitle" ? "Sottotitolo anteprima" : "Kicker anteprima"}
              onBlur={(e) =>
                onPreviewTextChange?.({
                  [layer.role]: e.currentTarget.textContent ?? "",
                })
              }
            >
              {layer.text}
            </span>
          ) : (
            layer.text
          );
          return (
            <div
              key={layer.role}
              className="style-preview-layer"
              style={{
                left: `${safe.x * 100}%`,
                top: `${layer.y * 100}%`,
                width: `${safe.w * 100}%`,
                height: `${layer.h * 100}%`,
                fontFamily: tr.font,
                fontWeight: tr.weight,
                fontSize: `${(tr.sizeRel * 100).toFixed(1)}cqh`,
                color,
                textAlign: tr.align,
                lineHeight: tr.lineHeight,
                letterSpacing: tr.tracking ? `${tr.tracking}em` : undefined,
                textTransform: tr.case === "upper" ? "uppercase" : undefined,
              }}
            >
              {content}
            </div>
          );
        })}
      </div>
      {editable && (
        <div className="style-preview-fields">
          <label className="field-label">
            Titolo anteprima
            <input
              type="text"
              value={texts.title}
              onChange={(e) => onPreviewTextChange?.({ title: e.target.value })}
            />
          </label>
          <label className="field-label">
            Sottotitolo anteprima
            <input
              type="text"
              value={texts.subtitle}
              onChange={(e) => onPreviewTextChange?.({ subtitle: e.target.value })}
            />
          </label>
        </div>
      )}
    </div>
  );
}
